<template>
  <div v-if="editor" class="email-template-tiptap">
    <div class="tiptap-toolbar" role="toolbar" aria-label="Rich text formatting">
      <div v-if="viewMode === 'design'" class="tiptap-toolbar__groups">
        <div class="tiptap-toolbar__group" role="group" aria-label="Text style">
          <button
            type="button"
            class="tiptap-toolbar__btn"
            :class="{ 'tiptap-toolbar__btn--active': editor.isActive('bold') }"
            title="Bold"
            aria-label="Bold"
            @click="editor.chain().focus().toggleBold().run()"
          >
            <i class="fas fa-bold" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            class="tiptap-toolbar__btn"
            :class="{ 'tiptap-toolbar__btn--active': editor.isActive('italic') }"
            title="Italic"
            aria-label="Italic"
            @click="editor.chain().focus().toggleItalic().run()"
          >
            <i class="fas fa-italic" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            class="tiptap-toolbar__btn"
            :class="{ 'tiptap-toolbar__btn--active': editor.isActive('strike') }"
            title="Strikethrough"
            aria-label="Strikethrough"
            @click="editor.chain().focus().toggleStrike().run()"
          >
            <i class="fas fa-strikethrough" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            class="tiptap-toolbar__btn"
            :class="{
              'tiptap-toolbar__btn--active': editor.isActive('underline')
            }"
            title="Underline"
            aria-label="Underline"
            @click="editor.chain().focus().toggleUnderline().run()"
          >
            <i class="fas fa-underline" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            class="tiptap-toolbar__btn"
            :class="{ 'tiptap-toolbar__btn--active': editor.isActive('code') }"
            title="Inline code"
            aria-label="Inline code"
            @click="editor.chain().focus().toggleCode().run()"
          >
            <i class="fas fa-code" aria-hidden="true"></i>
          </button>
        </div>

        <span class="tiptap-toolbar__sep" aria-hidden="true"></span>

        <div class="tiptap-toolbar__group" role="group" aria-label="Font size">
          <label class="tiptap-toolbar__fs-label mb-0">
            <span class="sr-only">Font size</span>
            <select
              class="tiptap-toolbar__select"
              :value="getTextStyleFontSize()"
              title="Font size"
              @change="onFontSizeChange"
            >
              <option value="">Default</option>
              <option value="10px">10px</option>
              <option value="12px">12px</option>
              <option value="14px">14px</option>
              <option value="16px">16px</option>
              <option value="18px">18px</option>
              <option value="20px">20px</option>
              <option value="24px">24px</option>
              <option value="28px">28px</option>
              <option value="32px">32px</option>
            </select>
          </label>
        </div>

        <span class="tiptap-toolbar__sep" aria-hidden="true"></span>

        <div class="tiptap-toolbar__group" role="group" aria-label="Headings">
          <button
            type="button"
            class="tiptap-toolbar__btn tiptap-toolbar__btn--text"
            :class="{
              'tiptap-toolbar__btn--active': editor.isActive('heading', {
                level: 2
              })
            }"
            title="Heading 2"
            aria-label="Heading 2"
            @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
          >
            H2
          </button>
          <button
            type="button"
            class="tiptap-toolbar__btn tiptap-toolbar__btn--text"
            :class="{
              'tiptap-toolbar__btn--active': editor.isActive('heading', {
                level: 3
              })
            }"
            title="Heading 3"
            aria-label="Heading 3"
            @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
          >
            H3
          </button>
          <button
            type="button"
            class="tiptap-toolbar__btn tiptap-toolbar__btn--text"
            :class="{
              'tiptap-toolbar__btn--active': isParagraphActive()
            }"
            title="Paragraph"
            aria-label="Paragraph"
            @click="editor.chain().focus().setParagraph().run()"
          >
            ¶
          </button>
        </div>

        <span class="tiptap-toolbar__sep" aria-hidden="true"></span>

        <div class="tiptap-toolbar__group" role="group" aria-label="Lists">
          <button
            type="button"
            class="tiptap-toolbar__btn"
            :class="{
              'tiptap-toolbar__btn--active': editor.isActive('bulletList')
            }"
            title="Bullet list"
            aria-label="Bullet list"
            @click="editor.chain().focus().toggleBulletList().run()"
          >
            <i class="fas fa-list-ul" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            class="tiptap-toolbar__btn"
            :class="{
              'tiptap-toolbar__btn--active': editor.isActive('orderedList')
            }"
            title="Numbered list"
            aria-label="Numbered list"
            @click="editor.chain().focus().toggleOrderedList().run()"
          >
            <i class="fas fa-list-ol" aria-hidden="true"></i>
          </button>
        </div>

        <span class="tiptap-toolbar__sep" aria-hidden="true"></span>

        <div class="tiptap-toolbar__group" role="group" aria-label="Blocks">
          <button
            type="button"
            class="tiptap-toolbar__btn"
            :class="{
              'tiptap-toolbar__btn--active': editor.isActive('blockquote')
            }"
            title="Blockquote"
            aria-label="Blockquote"
            @click="editor.chain().focus().toggleBlockquote().run()"
          >
            <i class="fas fa-quote-right" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            class="tiptap-toolbar__btn"
            :class="{
              'tiptap-toolbar__btn--active': editor.isActive('codeBlock')
            }"
            title="Code block"
            aria-label="Code block"
            @click="editor.chain().focus().toggleCodeBlock().run()"
          >
            <i class="fas fa-file-code" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            class="tiptap-toolbar__btn"
            title="Horizontal rule"
            aria-label="Horizontal rule"
            @click="editor.chain().focus().setHorizontalRule().run()"
          >
            <i class="fas fa-minus" aria-hidden="true"></i>
          </button>
        </div>

        <span class="tiptap-toolbar__sep" aria-hidden="true"></span>

        <div class="tiptap-toolbar__group" role="group" aria-label="Alignment">
          <button
            type="button"
            class="tiptap-toolbar__btn"
            :class="{
              'tiptap-toolbar__btn--active': editor.isActive({
                textAlign: 'left'
              })
            }"
            title="Align left"
            aria-label="Align left"
            @click="editor.chain().focus().setTextAlign('left').run()"
          >
            <i class="fas fa-align-left" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            class="tiptap-toolbar__btn"
            :class="{
              'tiptap-toolbar__btn--active': editor.isActive({
                textAlign: 'center'
              })
            }"
            title="Align center"
            aria-label="Align center"
            @click="editor.chain().focus().setTextAlign('center').run()"
          >
            <i class="fas fa-align-center" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            class="tiptap-toolbar__btn"
            :class="{
              'tiptap-toolbar__btn--active': editor.isActive({
                textAlign: 'right'
              })
            }"
            title="Align right"
            aria-label="Align right"
            @click="editor.chain().focus().setTextAlign('right').run()"
          >
            <i class="fas fa-align-right" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            class="tiptap-toolbar__btn"
            :class="{
              'tiptap-toolbar__btn--active': editor.isActive({
                textAlign: 'justify'
              })
            }"
            title="Justify"
            aria-label="Justify"
            @click="editor.chain().focus().setTextAlign('justify').run()"
          >
            <i class="fas fa-align-justify" aria-hidden="true"></i>
          </button>
        </div>

        <span class="tiptap-toolbar__sep" aria-hidden="true"></span>

        <div class="tiptap-toolbar__group" role="group" aria-label="Insert">
          <button
            type="button"
            class="tiptap-toolbar__btn"
            title="Insert link"
            aria-label="Insert link"
            @click="setLink"
          >
            <i class="fas fa-link" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            class="tiptap-toolbar__btn"
            title="Insert image"
            aria-label="Insert image"
            @click="addImage"
          >
            <i class="fas fa-image" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            class="tiptap-toolbar__btn"
            title="Insert table"
            aria-label="Insert table"
            @click="
              editor
                .chain()
                .focus()
                .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                .run()
            "
          >
            <i class="fas fa-table" aria-hidden="true"></i>
          </button>
        </div>

        <span class="tiptap-toolbar__sep" aria-hidden="true"></span>

        <div class="tiptap-toolbar__group" role="group" aria-label="History">
          <button
            type="button"
            class="tiptap-toolbar__btn"
            title="Undo"
            aria-label="Undo"
            :disabled="!editor.can().undo()"
            @click="editor.chain().focus().undo().run()"
          >
            <i class="fas fa-undo" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            class="tiptap-toolbar__btn"
            title="Redo"
            aria-label="Redo"
            :disabled="!editor.can().redo()"
            @click="editor.chain().focus().redo().run()"
          >
            <i class="fas fa-redo" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <div class="tiptap-toolbar__view-toggle" role="group" aria-label="Editor view">
        <button
          type="button"
          class="tiptap-toolbar__btn tiptap-toolbar__btn--text"
          :class="{ 'tiptap-toolbar__btn--active': viewMode === 'design' }"
          title="Design view"
          aria-label="Design view"
          @click="setViewMode('design')"
        >
          Design
        </button>
        <button
          type="button"
          class="tiptap-toolbar__btn tiptap-toolbar__btn--text"
          :class="{ 'tiptap-toolbar__btn--active': viewMode === 'html' }"
          title="HTML view"
          aria-label="HTML view"
          @click="setViewMode('html')"
        >
          HTML
        </button>
      </div>
    </div>

    <editor-content
      v-show="viewMode === 'design'"
      :editor="editor"
      class="tiptap-editor-content"
    />
    <textarea
      v-show="viewMode === 'html'"
      v-model="htmlSource"
      class="tiptap-html-source"
      spellcheck="false"
      aria-label="HTML source"
      @input="onHtmlSourceInput"
    />
  </div>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-2";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextStyle from "@tiptap/extension-text-style";
import TextAlign from "@tiptap/extension-text-align";
import { FontSize } from "./tiptapFontSizeExtension";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import Placeholder from "@tiptap/extension-placeholder";

export default {
  name: "EmailTemplateTipTapEditor",
  components: {
    EditorContent
  },
  props: {
    value: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      editor: null,
      viewMode: "design",
      htmlSource: ""
    };
  },
  watch: {
    value(next) {
      if (!this.editor) return;
      const current =
        this.viewMode === "html"
          ? this.htmlSource
          : this.editor.getHTML();
      if (next === current) return;
      if (this.viewMode === "html") {
        this.htmlSource = next || "";
        return;
      }
      this.editor.commands.setContent(next || "<p></p>", false);
    }
  },
  mounted() {
    this.htmlSource = this.value || "";
    this.editor = new Editor({
      content: this.value || "<p></p>",
      extensions: [
        StarterKit.configure({
          heading: { levels: [2, 3] }
        }),
        Underline,
        TextStyle,
        FontSize,
        TextAlign.configure({
          types: ["heading", "paragraph"]
        }),
        Link.configure({
          openOnClick: false,
          defaultProtocol: "https"
        }),
        Image,
        Table.configure({
          resizable: false
        }),
        TableRow,
        TableHeader,
        TableCell,
        Placeholder.configure({
          placeholder: "Start writing here."
        })
      ],
      onUpdate: () => {
        this.$emit("input", this.editor.getHTML());
      },
      onTransaction: () => {
        this.$forceUpdate();
      }
    });
  },
  beforeDestroy() {
    if (this.editor) {
      this.editor.destroy();
    }
  },
  methods: {
    setViewMode(mode) {
      if (mode === this.viewMode) return;

      if (mode === "html") {
        this.htmlSource = this.editor
          ? this.editor.getHTML()
          : this.value || "";
        this.$emit("input", this.htmlSource);
      } else {
        const content = this.htmlSource || this.value || "<p></p>";
        if (this.editor) {
          this.editor.commands.setContent(content, false);
        }
        this.$emit("input", content);
      }

      this.viewMode = mode;
    },
    onHtmlSourceInput() {
      this.$emit("input", this.htmlSource);
    },
    /** Paragraph tool: highlight only in plain paragraph (not heading/list/block). */
    isParagraphActive() {
      const e = this.editor;
      return (
        e.isActive("paragraph") &&
        !e.isActive("heading") &&
        !e.isActive("bulletList") &&
        !e.isActive("orderedList") &&
        !e.isActive("blockquote") &&
        !e.isActive("codeBlock")
      );
    },
    setLink() {
      const previous = this.editor.getAttributes("link").href;
      const url = window.prompt("URL", previous || "https://");
      if (url === null) return;
      if (url === "") {
        this.editor.chain().focus().extendMarkRange("link").unsetLink().run();
        return;
      }
      this.editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    },
    addImage() {
      const url = window.prompt("Image URL", "https://");
      if (!url) return;
      this.editor.chain().focus().setImage({ src: url }).run();
    },
    getTextStyleFontSize() {
      if (!this.editor) return "";
      const { fontSize } = this.editor.getAttributes("textStyle");
      return fontSize || "";
    },
    onFontSizeChange(evt) {
      const v = evt.target.value;
      if (!v) {
        this.editor.chain().focus().unsetFontSize().run();
      } else {
        this.editor.chain().focus().setFontSize(v).run();
      }
    }
  }
};
</script>

<style lang="scss">
.email-template-tiptap {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;

  .tiptap-toolbar {
    background: #f7f8fa;
    border-bottom: 1px solid #dee2e6;
    padding: 6px 8px;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
  }

  .tiptap-toolbar__view-toggle {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-left: auto;
    flex-shrink: 0;
  }

  .tiptap-html-source {
    box-sizing: border-box;
    width: 100%;
    min-height: 300px;
    border: none;
    border-radius: 0;
    padding: 12px 14px;
    font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono",
      "Courier New", monospace;
    font-size: 0.8125rem;
    line-height: 1.5;
    color: #32325d;
    background: #fff;
    resize: vertical;
    text-align: left;

    &:focus {
      outline: none;
    }
  }

  .tiptap-toolbar__groups {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 8px;
  }

  .tiptap-toolbar__group {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
  }

  .tiptap-toolbar__sep {
    display: inline-block;
    width: 1px;
    height: 22px;
    background: #dee2e6;
    flex-shrink: 0;
    align-self: center;
  }

  .tiptap-toolbar__fs-label {
    display: inline-flex;
    align-items: center;
  }

  .tiptap-toolbar__select {
    box-sizing: border-box;
    height: 2rem;
    min-width: 5.75rem;
    padding: 0 1.75rem 0 0.4rem;
    font-size: 0.8125rem;
    line-height: 1.2;
    color: #525f7f;
    background-color: #fff;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    cursor: pointer;
    appearance: auto;

    &:focus {
      outline: none;
      border-color: #5e72e4;
      box-shadow: 0 0 0 2px rgba(94, 114, 228, 0.2);
    }
  }

  .tiptap-toolbar__btn {
    box-sizing: border-box;
    min-width: 2rem;
    height: 2rem;
    padding: 0 0.4rem;
    margin: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    background: #fff;
    color: #525f7f;
    font-size: 0.8125rem;
    line-height: 1;
    cursor: pointer;
    transition:
      background 0.12s ease,
      border-color 0.12s ease,
      color 0.12s ease;

    &:hover:not(:disabled) {
      background: #edf2f7;
      border-color: #cbd3da;
      color: #32325d;
    }

    &:focus {
      outline: none;
      border-color: #5e72e4;
      box-shadow: 0 0 0 2px rgba(94, 114, 228, 0.2);
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    &--active {
      background: #e8ecf4;
      border-color: #5e72e4;
      color: #324cdd;
    }

    &--text {
      font-weight: 600;
      font-size: 0.75rem;
    }

    i {
      font-size: 0.875rem;
      pointer-events: none;
    }
  }

  .tiptap-editor-content .ProseMirror {
    min-height: 300px;
    border: none;
    border-radius: 0;
    padding: 12px 14px;
    text-align: left;

    &:focus {
      outline: none;
    }

    table {
      border-collapse: collapse;
      width: 100%;
      margin: 0.5rem 0;
      td,
      th {
        border: 1px solid #ced4da;
        padding: 6px 8px;
        vertical-align: top;
      }
      th {
        background: #f8f9fa;
        font-weight: 600;
      }
    }

    pre {
      background: #f8f9fa;
      padding: 0.75rem;
      border-radius: 4px;
      overflow-x: auto;
    }

    blockquote {
      border-left: 4px solid #dee2e6;
      margin-left: 0;
      padding-left: 1rem;
      color: #6c757d;
    }
  }
}
</style>
